import { useState } from 'react'
import { useSelector } from 'react-redux'
import { companyIdSelector } from 'ducks/user'
import { createMultipleUsersService } from 'views/screens/Platform-administrator/services'
import { useNotification } from 'lib/context/notification.context'
import { isAxiosError } from 'axios'
import { CreateUserBatch } from 'types/common'

const DEFAULT_EVALUATED_USER_ROLE_TYPE = 4

interface UserData {
  firstName?: string
  lastName?: string
  first_name?: string
  last_name?: string
  document: string
  email: string
  documentType: number
  reason?: string
}

interface Result {
  user: UserData
  status: string
  error?: any
}

const mapFailedUsers = (users: CreateUserBatch[]): Result[] =>
  users.map((user: CreateUserBatch) => ({
    user: {
      firstName: user.first_name,
      lastName: user.last_name,
      document: user.document,
      email: user.email,
      documentType: user.document_type,
      reason: user.reason,
    },
    status: 'error',
  }))

const useCreateUsersBatch = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<any>(null)
  const [resultsSucess, setResultsSucces] = useState<UserData[]>([])
  const [resultsFail, setResultsFail] = useState<Result[]>([])
  const [urlExcelUsersFailed, setUrlExcelUsersFailed] = useState<string>('')
  const { getWarning } = useNotification()

  const companyId = useSelector(companyIdSelector)

  const createUsersBatch = async (users: UserData[], process_id?: number) => {
    setLoading(true)
    setError(null)
    setResultsSucces([])
    setResultsFail([])
    setUrlExcelUsersFailed('')

    const userList = users.map((user: UserData) => {
      return {
        first_name: user.firstName,
        last_name: user.lastName,
        document: String(user.document ?? '').trim(),
        email: String(user.email ?? '').trim().toLowerCase(),
        document_type: user.documentType,
        role_type: DEFAULT_EVALUATED_USER_ROLE_TYPE,
        company_id: companyId,
      }
    })

    const body = {
      users: userList,
      process_id,
    }

    try {
      const response = await createMultipleUsersService(body)

      const resultsSuccess = response.data.addedUsers.map((user) => {
        return {
          id: user.user_id,
          userId: user.user_id,
          firstName: user.first_name,
          lastName: user.last_name,
          document: user.document,
          email: user.email,
          documentType: user.document_type,
        }
      })

      const resultsFailed = mapFailedUsers(response.data.failedUsers)

      setResultsSucces(resultsSuccess)
      setResultsFail(resultsFailed)
      setUrlExcelUsersFailed(response.data.failed_users_file || '')

      if (response?.data?.alternative_inventory) {
        getWarning(
          `Los créditos se descontarán del inventario ${response.data.alternative_inventory.name}.`,
        )
      }

      setLoading(false)

      // Retornar los datos directamente para que puedan ser usados inmediatamente en la función que llama
      return {
        resultsSucess: resultsSuccess,
        resultsFail: resultsFailed,
        urlExcelUsersFailed: response.data.failed_users_file || '',
      }
    } catch (e) {
      const apiError = isAxiosError<{ message?: string; failedUsers?: CreateUserBatch[] }>(e)
        ? e.response?.data
        : undefined
      setResultsFail(mapFailedUsers(apiError?.failedUsers || []))
      const message = apiError?.message || (e instanceof Error ? e.message : 'Error al procesar el archivo.')
      setError(message)
      setLoading(false)
      throw new Error(message)
    }
  }

  return { createUsersBatch, loading, error, resultsSucess, resultsFail, urlExcelUsersFailed }
}

export default useCreateUsersBatch
